// Map view: Leaflet + OpenStreetMap tiles. One marker per event that has
// coordinates. Markers are colored by category. Clicking a list item focuses
// the matching marker. Panning/zooming reveals a "Search this area" button that
// re-runs the search around the new map center. Reads the same result set as
// the other views.

import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { categoryMeta } from '../lib/categories';
import { RADIUS_OPTIONS } from '../lib/constants';
import { formatDateTime } from '../lib/format';
import { store, visibleEvents, type AppState } from '../lib/state';
import type { NormalizedEvent } from '../types/event';

// OSM usage policy asks for attribution and reasonable request volume.
const TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const TILE_ATTRIB = '&copy; OpenStreetMap contributors';

export interface MapHandlers {
  runSearch: () => Promise<void>;
}

// Round the visible radius up to the smallest configured option that covers it,
// so the fetch spans the whole viewport and the radius controls stay in sync.
function snapRadius(miles: number): number {
  for (const r of RADIUS_OPTIONS) if (r >= miles) return r;
  return RADIUS_OPTIONS[RADIUS_OPTIONS.length - 1];
}

export function createMapView(root: HTMLElement, handlers: MapHandlers): void {
  root.classList.add('map-wrap');
  root.innerHTML = '<div id="map" class="map-canvas" aria-label="Map of events"></div>';
  const mapEl = root.querySelector<HTMLDivElement>('#map')!;

  const searchAreaBtn = document.createElement('button');
  searchAreaBtn.type = 'button';
  searchAreaBtn.className = 'map-search-area';
  searchAreaBtn.hidden = true;
  searchAreaBtn.innerHTML =
    '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/></svg><span>Search this area</span>';
  root.appendChild(searchAreaBtn);

  let map: L.Map | null = null;
  const markers = new Map<string, L.CircleMarker>();
  let lastSignature = '';
  // Ignore moveend events caused by our own programmatic recenters, so the
  // button only appears after a genuine user pan/zoom.
  let suppressUntil = 0;

  function recenter(fn: () => void): void {
    suppressUntil = Date.now() + 700;
    fn();
  }

  function ensureMap(): L.Map {
    if (map) return map;
    map = L.map(mapEl, { scrollWheelZoom: true }).setView([39.7392, -104.9903], 11);
    L.tileLayer(TILE_URL, { attribution: TILE_ATTRIB, maxZoom: 19 }).addTo(map);
    L.DomEvent.disableClickPropagation(searchAreaBtn);
    map.on('moveend', () => {
      if (Date.now() < suppressUntil) return;
      searchAreaBtn.hidden = false;
    });
    return map;
  }

  function searchThisArea(): void {
    const m = ensureMap();
    const center = m.getCenter();
    const miles = m.distance(center, m.getBounds().getNorthEast()) / 1609.344;
    searchAreaBtn.hidden = true;
    store.set({
      location: { lat: center.lat, lng: center.lng, label: 'Map area' },
      radiusMiles: snapRadius(miles),
      selectedDay: undefined,
    });
    void handlers.runSearch();
  }
  searchAreaBtn.addEventListener('click', searchThisArea);

  function escapeHtml(s: string): string {
    return s.replace(
      /[&<>"']/g,
      (c) =>
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!
    );
  }

  function popupHtml(e: NormalizedEvent): string {
    const venue = [e.venueName, e.city].filter(Boolean).join(', ');
    const link = `<a href="${escapeHtml(e.sourceUrl)}" target="_blank" rel="noopener noreferrer">View details</a>`;
    return `
      <div class="map-popup">
        <strong>${escapeHtml(e.title)}</strong><br/>
        <span>${escapeHtml(formatDateTime(e.start))}</span><br/>
        ${venue ? `<span>${escapeHtml(venue)}</span><br/>` : ''}
        ${link}
      </div>`;
  }

  function renderMarkers(state: AppState): void {
    const m = ensureMap();
    const events = visibleEvents(state).filter(
      (e) => typeof e.lat === 'number' && typeof e.lng === 'number'
    );

    // Cheap change detection so we do not rebuild markers on every tick.
    const signature = events.map((e) => e.id).join(',') + `|${state.selectedDay ?? ''}`;
    if (signature !== lastSignature) {
      lastSignature = signature;
      for (const marker of markers.values()) marker.remove();
      markers.clear();

      const bounds: L.LatLngExpression[] = [];
      for (const e of events) {
        const color = categoryMeta(e.category).color;
        const marker = L.circleMarker([e.lat!, e.lng!], {
          radius: 8,
          color: '#fff',
          weight: 2,
          fillColor: color,
          fillOpacity: 0.9,
        });
        marker.bindPopup(popupHtml(e));
        marker.on('click', () => store.set({ selectedEventId: e.id }));
        marker.addTo(m);
        markers.set(e.id, marker);
        bounds.push([e.lat!, e.lng!]);
      }

      // New results recenter the map; mark these as programmatic so the
      // "Search this area" button does not pop up from our own movement.
      if (bounds.length > 0) {
        recenter(() =>
          m.fitBounds(bounds as L.LatLngBoundsLiteral, { padding: [40, 40], maxZoom: 14 })
        );
      } else if (state.location) {
        recenter(() => m.setView([state.location!.lat, state.location!.lng], 11));
      }
    }

    // Focus a marker when a list item is clicked.
    if (state.focusEventId) {
      const marker = markers.get(state.focusEventId);
      if (marker) {
        recenter(() =>
          m.setView(marker.getLatLng(), Math.max(m.getZoom(), 14), { animate: true })
        );
        marker.openPopup();
      }
      // Consume the transient focus request.
      store.set({ focusEventId: undefined });
    }
  }

  store.subscribe((state) => {
    if (state.activeView !== 'map') return;
    renderMarkers(state);
    // Leaflet needs a size recompute after the container becomes visible.
    requestAnimationFrame(() => map?.invalidateSize());
  });
}
